import type { GamePinType, GamePinView, HuntPlanParticipant } from '../../lib/types'
import type { HuntableGameSpecies } from '../../lib/huntable-game-catalog'
import { speciesDisplayName } from '../../lib/huntable-game-catalog'
// Reused rather than reimplemented: the plan-hunt / report avatars already derive
// initials the same way, and a second copy would drift.
import { initialsOf } from '../planHunt/PlanHuntAvatar'

/**
 * Head-of-game per report type. Animals, not pins — one pin can carry several,
 * and the report counts the animals, matching the stat bar's own tally.
 */
export interface GameCounts {
  observed: number
  downed: number
  wounded: number
}

/** Animals on a pin: one pin can carry several. */
function animalCount(pin: GamePinView): number {
  return pin.games?.length ?? 0
}

/**
 * Tally the animals by the pin's REAL type, so observed / downed / wounded stay
 * distinct. The earlier report logic treated anything not downed as wounded,
 * which reported a sighting as an outstanding tracking job.
 */
export function countGamePins(pins: readonly GamePinView[]): GameCounts {
  const counts: GameCounts = { observed: 0, downed: 0, wounded: 0 }
  for (const pin of pins) counts[pin.pinType] += animalCount(pin)
  return counts
}

/**
 * The initials of whoever the pin is credited to — the explicit shooter, else
 * the reporter — resolved against the hunt's participants. "?" when that person
 * is not on the roster (a manager who isn't a participant, a departed member) so
 * the row still renders an avatar rather than a blank.
 */
export function reporterInitials(pin: GamePinView, participants: readonly HuntPlanParticipant[]): string {
  const creditedTo = pin.shooterId ?? pin.reporterId
  const person = participants.find((p) => p.userId === creditedTo)
  if (!person || person.userName.trim() === '') return '?'
  return initialsOf(person.userName)
}

/**
 * The pin's report time as HH:mm in the viewer's locale, or '' when the pin
 * carries no (or an unparseable) date. Production sends an ISO instant; the time
 * shown is the viewer's local time of that instant.
 */
export function formatPinTime(date: string | null | undefined, locale: string): string {
  if (!date) return ''
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return ''
  // `hour12: false` guarantees the HH:mm the design asks for rather than an
  // AM/PM rendering some locales default to.
  return parsed.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
}

/**
 * The pin's species as a row heading: the first animal's localized name, plus a
 * `+N` when the pin carries more than one. '' for a pin with no animals.
 */
export function speciesLabel(pin: GamePinView, catalog: readonly HuntableGameSpecies[]): string {
  const games = pin.games ?? []
  if (games.length === 0) return ''
  const first = speciesDisplayName(catalog, games[0].name)
  return games.length > 1 ? `${first} +${games.length - 1}` : first
}

/** A date's epoch ms, or +Infinity when it is missing/unparseable, so undated
 *  pins sort after every dated one rather than jumping to the front. */
function timeKey(date: string | null | undefined): number {
  if (!date) return Number.POSITIVE_INFINITY
  const ms = Date.parse(date)
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms
}

/** A copy of the pins ordered by report time ascending, undated pins last. Does
 *  not mutate its input. */
export function sortPinsByTime(pins: readonly GamePinView[]): GamePinView[] {
  return [...pins].sort((a, b) => timeKey(a.date) - timeKey(b.date))
}

/** i18n keys (in the `huntInvitation` namespace) for the row subtitle label per
 *  pin type. Downed / wounded reuse the shipped downed-game labels; observed has
 *  its own "Observation" wording. */
export const ROW_TYPE_LABEL_KEY: Record<GamePinType, string> = {
  observed: 'gameReports.typeObservation',
  downed: 'downedGame.typeDowned',
  wounded: 'downedGame.typeWounded',
}
